/**
 * Signin API Hook
 *
 * Manages state and logic for the signin authentication flow.
 * Delegates network calls to the API service layer.
 */

import { useState, useCallback, useRef } from "react";
import { useAuth } from "../core/auth-context";
import { mapApiError } from "../core/errors";
import { ERROR_CODES, STORAGE_KEYS, env, API_ENDPOINTS } from "@monorepo/config";
import { cookieManager } from "../core/cookie-manager";
import {
  signinIdentify,
  signinPassword,
  getMfaMethods,
  signinVerifyTotp,
  signinVerifyBackupCode,
  getWebAuthnAssertionOptions,
  verifyWebAuthnAssertion,
  signinForceChangePassword,
  signinInitMfaSetup,
  signinCompleteMfaSetup,
  signinGenerateBackupCodes,
  signinConfirmBackupCodes,
  signinSkipMfa,
} from "../core/api";

import type {
  SigninIdentifyResponse,
  SigninLoginRequest,
  SigninLoginResponse,
  SigninGetMfaMethodsResponse,
  SigninVerifyTotpResponse,
  SigninVerifyBackupCodeResponse,
  SigninWebAuthnAssertionOptionsResponse,
  SigninVerifyWebAuthnAssertionResponse,
  SigninVerifyWebAuthnAssertionRequest,
  SigninForceChangePasswordResponse,
  SigninMfaSetupInitResponse,
  SigninMfaSetupConfirmResponse,
  SigninGenerateBackupCodesResponse,
  SigninConfirmBackupCodesResponse,
  SigninUser,
} from "./types";

const USE_MOCKS =
  import.meta.env.DEV && import.meta.env.VITE_USE_SIGNIN_MOCKS === "true";

const handleError = (error: unknown) => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    (error as any).response
  ) {
    const axiosError = error as {
      response: { data?: unknown; status?: number };
    };
    return mapApiError(
      axiosError.response.data || error,
      axiosError.response.status
    );
  }
  return mapApiError(error);
};

// Standardized Error for Invalid Server Responses
const INVALID_RESPONSE_ERROR = {
  code: ERROR_CODES.SERVER_ERROR,
  message: "The server returned an invalid response. Please try again.",
  status: 500,
};

// HELPER: Map SigninUser to AuthContext User (Handling optional fields)
// This bridges the type gap between the API response (optional fields) and AuthContext (required fields)
const mapUserToContext = (apiUser: SigninUser) => {
  return {
    ...apiUser,
    companyName: apiUser.companyName || "", // Default to empty string if undefined
    name: apiUser.name || apiUser.id, // Ensure name is populated, fallback to ID
    hasMfa: apiUser.hasMfa ?? false, // Ensure boolean
  };
};

export function useSigninApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const isExecutingRef = useRef(false);

  // Added setUser to update global auth state on success
  const { setPreMfaToken, preMfaToken, setUser } = useAuth();

  const clearError = useCallback(() => setError(null), []);

  /**
   * Centralized processing for all successful sign-in responses.
   * Handles user context update and cookie storage for Authentik sessions.
   */
  const processSuccessResponse = useCallback(
    (response: any) => {
      // 1. Update Auth Context if user provided
      if (response.user) {
        setUser(mapUserToContext(response.user));
      }

      // 2. Store session tokens in cookies
      // We MUST use the root domain (.sagasoft.xyz) so that auth.sagasoft.xyz can see them.
      // NOTE: If you have an existing cookie on auth.sagasoft.xyz, the browser will prefer that
      // over this root cookie. You MUST clear your browser cookies for auth.sagasoft.xyz.
      const hostname = typeof window !== "undefined" ? window.location.hostname : "";
      const hostnameParts = hostname.split(".");
      const cookieDomain = hostnameParts.length >= 2 ? `.${hostnameParts.slice(-2).join(".")}` : "";

      const setSessionCookie = (name: string, value: string) => {
        // FIX: Clear the cookie from the current specific subdomain first
        // to prevent "multiple cookies with name" error when setting on root domain.
        cookieManager.clearCurrent(name);
        cookieManager.set(name, value, 1, cookieDomain);
      };

      if (response.authentik_session) {
        setSessionCookie(STORAGE_KEYS.AUTHENTIK_SESSION, response.authentik_session);
      }
      if (response.authentik_csrf) {
        setSessionCookie(STORAGE_KEYS.AUTHENTIK_CSRF, response.authentik_csrf);
      }
      if (response.user && response.user.email) {
        setSessionCookie(STORAGE_KEYS.USER_EMAIL, response.user.email);
      }

      // 3. Flutter App Bridge (Seamless Automatic Push)
      if (typeof window !== "undefined") {
        try {
          const payload = { status: "ok", ...response };
          const message = JSON.stringify({ type: "AUTH_SUCCESS", payload: payload });
          
          if ((window as any).FlutterJSChannel) {
            (window as any).FlutterJSChannel.postMessage(message);
          } else {
            window.postMessage(message, "*");
          }
        } catch (e) {
          console.warn("Seamless bridge push failed", e);
        }
      }
    },
    [setUser]
  );

  const execute = useCallback(
    async <T>(fn: () => Promise<T>, mockFn?: () => Promise<T>): Promise<T> => {
      if (isExecutingRef.current) {
        return new Promise(() => { }); // Prevent concurrent executions
      }
      isExecutingRef.current = true;
      setLoading(true);
      setError(null);
      try {
        if (USE_MOCKS && mockFn) {
          return await mockFn();
        }
        return await fn();
      } catch (err: unknown) {
        const normalizedError = handleError(err);
        setError(normalizedError);
        throw normalizedError;
      } finally {
        setLoading(false);
        isExecutingRef.current = false;
      }
    },
    []
  );

  // ==========================================
  // STEP 1: IDENTIFY (Legacy/Optional)
  // ==========================================
  const identify = useCallback(
    async (
      email: string,
      captchaToken: string
    ): Promise<SigninIdentifyResponse> => {
      return execute(
        async () => {
          if (!captchaToken) {
            throw new Error("Captcha token is required for identification.");
          }
          const response = await signinIdentify({
            email,
            captchaToken,
          });
          if (!response || !response.mode) {
            throw INVALID_RESPONSE_ERROR;
          }
          return response;
        },
        async () => {
          const { mockIdentify } = await import("./mockSigninApi");
          return mockIdentify({ email, captchaToken });
        }
      );
    },
    [execute]
  );

  // ==========================================
  // STEP 2: PASSWORD LOGIN (Merged Flow)
  // ==========================================
  const loginWithPassword = useCallback(
    async (params: SigninLoginRequest): Promise<SigninLoginResponse> => {
      return execute(
        async () => {
          if (!params.captchaToken) {
            console.warn(
              "Security Warning: Captcha token missing in login request"
            );
          }

          const response = await signinPassword(params);

          if (!response) {
            throw INVALID_RESPONSE_ERROR;
          }

          if ("mfa_required" in response && response.mfa_required) {
            if (!response.pre_mfa_token) {
              throw INVALID_RESPONSE_ERROR;
            }
            setPreMfaToken(response.pre_mfa_token);

            // USER REQUEST: Capture the intermediate session token even before MFA completion.
            // WARNING: This may trigger "multiple cookies" error if the backend also sets 
            // a cookie on the specific subdomain (e.g. api.sagasoft.xyz).
            processSuccessResponse(response);
          } else if ("status" in response && response.status === "ok") {
            // Success (No MFA) - Update Auth Context and Cookies
            processSuccessResponse(response);
          } else if (
            // Valid intermediate states (Password Change / MFA Setup)
            ("status" in response &&
              response.status === "password_change_required") ||
            ("status" in response && response.status === "mfa_setup_required")
          ) {
            // No user session yet, waiting for next step
          } else {
            throw INVALID_RESPONSE_ERROR;
          }

          return response;
        },
        async () => {
          const { mockLoginWithPassword } = await import("./mockSigninApi");
          const response = await mockLoginWithPassword(params);

          // Mirror real API path: update auth context from mock response
          if ("mfa_required" in response && response.mfa_required) {
            if (response.pre_mfa_token) setPreMfaToken(response.pre_mfa_token);
          } else if ("status" in response && response.status === "ok" && response.user) {
            setUser(mapUserToContext(response.user));
          }

          return response;
        }
      );
    },
    [execute, setPreMfaToken, setUser]
  );

  // ==========================================
  // FORCE PASSWORD CHANGE (Employee Onboarding)
  // ==========================================
  const forceChangePassword = useCallback(
    async (
      newPassword: string,
      preAuthToken: string
    ): Promise<SigninForceChangePasswordResponse> => {
      return execute(
        async () => {
          if (!preAuthToken) throw new Error("Missing auth token.");
          const response = await signinForceChangePassword(
            newPassword,
            preAuthToken
          );
          if (!response || !response.status) throw INVALID_RESPONSE_ERROR;

          // If password change logs user in directly (optional flow)
          if (response.status === "ok") {
            processSuccessResponse(response);
          }

          return response;
        },
        async () => {
          const { mockForceChangePassword } = await import("./mockSigninApi");
          return mockForceChangePassword({ newPassword, preAuthToken });
        }
      );
    },
    [execute, setUser]
  );

  // ==========================================
  // FORCE MFA SETUP (Employee Onboarding)
  // ==========================================
  const initMfaSetup = useCallback(
    async (preAuthToken: string): Promise<SigninMfaSetupInitResponse> => {
      return execute(
        async () => {
          if (!preAuthToken) throw new Error("Missing auth token.");
          const response = await signinInitMfaSetup(preAuthToken);
          if (!response || !response.secret) throw INVALID_RESPONSE_ERROR;
          return response;
        },
        async () => {
          const { mockInitMfaSetup } = await import("./mockSigninApi");
          return mockInitMfaSetup({ preAuthToken });
        }
      );
    },
    [execute]
  );

  const completeMfaSetup = useCallback(
    async (
      code: string,
      preAuthToken: string
    ): Promise<SigninMfaSetupConfirmResponse> => {
      return execute(
        async () => {
          if (!preAuthToken) throw new Error("Missing auth token.");
          const response = await signinCompleteMfaSetup(code, preAuthToken);
          if (!response || response.status !== "ok")
            throw INVALID_RESPONSE_ERROR;

          // Success - Update Auth Context and Cookies
          processSuccessResponse(response);

          return response;
        },
        async () => {
          const { mockCompleteMfaSetup } = await import("./mockSigninApi");
          const response = await mockCompleteMfaSetup({ code, preAuthToken });
          if (response.user) processSuccessResponse(response);
          return response;
        }
      );
    },
    [execute, setUser]
  );

  // ==========================================
  // BACKUP CODE SETUP (Employee Onboarding)
  // ==========================================
  const generateBackupCodes = useCallback(
    async (
      tokenOverride?: string
    ): Promise<SigninGenerateBackupCodesResponse> => {
      return execute(
        async () => {
          const token = tokenOverride || preMfaToken;
          if (!token)
            throw new Error("Missing auth token for backup generation.");

          const response = await signinGenerateBackupCodes(token);
          if (!response || !response.backup_codes) throw INVALID_RESPONSE_ERROR;
          return response;
        },
        // Mock fallback handled by api layer/mock if configured
        async () => {
          // We can import mock if needed, but usually real API is preferred structure
          const { mockGenerateBackupCodes } = await import("./mockSigninApi");
          return mockGenerateBackupCodes({
            pre_mfa_token: tokenOverride || preMfaToken || "mock_token",
          });
        }
      );
    },
    [execute, preMfaToken]
  );

  const confirmBackupCodesSaved = useCallback(
    async (
      tokenOverride?: string
    ): Promise<SigninConfirmBackupCodesResponse> => {
      return execute(
        async () => {
          const token = tokenOverride || preMfaToken;
          if (!token) throw new Error("Missing auth token for confirmation.");

          const response = await signinConfirmBackupCodes(token);
          if (!response || response.status !== "ok")
            throw INVALID_RESPONSE_ERROR;

          // Success - Update Auth Context (Final step of onboarding)
          processSuccessResponse(response);

          return response;
        },
        async () => {
          const { mockConfirmBackupCodes } = await import("./mockSigninApi");
          const response = await mockConfirmBackupCodes({
            pre_mfa_token: tokenOverride || preMfaToken || "mock_token",
          });
          if (response.user) processSuccessResponse(response);
          return response;
        }
      );
    },
    [execute, preMfaToken, setUser]
  );

  const skipMfaCall = useCallback(async (tokenOverride?: string, email?: string): Promise<any> => {
    return execute(
      async () => {
        const token = tokenOverride || preMfaToken;
        if (!token) throw new Error("Missing auth token for skipping MFA.");
        const response = await signinSkipMfa(token, email);
        if (!response || !response.success) throw INVALID_RESPONSE_ERROR;

        // Success - Update Auth Context and Cookies
        processSuccessResponse(response);

        return response;
      },
      async () => {
        const { mockSkipMfa } = await import("../signup/mockSignupApi");
        const response = await mockSkipMfa(preMfaToken || "mock-token");
        if ((response as any).user) processSuccessResponse(response);
        return response;
      }
    );
  }, [execute, preMfaToken, processSuccessResponse]);

  // ==========================================
  // MFA VERIFICATION METHODS
  // ==========================================
  const getMfaMethodsCall =
    useCallback(async (): Promise<SigninGetMfaMethodsResponse> => {
      return execute(
        async () => {
          if (!preMfaToken) throw new Error("Missing MFA token");
          const methods = await getMfaMethods(preMfaToken);
          if (!methods || !Array.isArray(methods)) throw INVALID_RESPONSE_ERROR;
          return { methods };
        },
        async () => {
          const { mockGetMfaMethods } = await import("./mockSigninApi");
          return mockGetMfaMethods({
            pre_mfa_token: preMfaToken || "mock_token",
          });
        }
      );
    }, [execute, preMfaToken]);

  const verifyTotp = useCallback(
    async (
      code: string,
      tokenOverride?: string
    ): Promise<SigninVerifyTotpResponse> => {
      return execute(
        async () => {
          const token = tokenOverride || preMfaToken;
          if (!token) throw new Error("Missing MFA token");

          const response = await signinVerifyTotp(code, token);
          if (!response || response.status !== "ok")
            throw INVALID_RESPONSE_ERROR;

          processSuccessResponse(response);
          return response;
        },
        async () => {
          const { mockVerifyTotp } = await import("./mockSigninApi");
          const response = await mockVerifyTotp({
            pre_mfa_token: tokenOverride || preMfaToken || "mock_token",
            code,
          });
          if (response.user) processSuccessResponse(response);
          return response;
        }
      );
    },
    [execute, preMfaToken, setUser]
  );

  // =========================================================================
  // FIX: Updated verifyBackupCode to accept tokenOverride
  // =========================================================================
  const verifyBackupCode = useCallback(
    async (
      code: string,
      tokenOverride?: string
    ): Promise<SigninVerifyBackupCodeResponse> => {
      return execute(
        async () => {
          const token = tokenOverride || preMfaToken;
          if (!token) throw new Error("Missing MFA token");

          const response = await signinVerifyBackupCode(code, token);
          if (!response || response.status !== "ok")
            throw INVALID_RESPONSE_ERROR;

          processSuccessResponse(response);
          return response;
        },
        async () => {
          const { mockVerifyBackupCode } = await import("./mockSigninApi");
          const response = await mockVerifyBackupCode({
            pre_mfa_token: tokenOverride || preMfaToken || "mock_token",
            code,
          });
          if (response.user) processSuccessResponse(response);
          return response;
        }
      );
    },
    [execute, preMfaToken, setUser]
  );

  const getWebAuthnAssertionOptionsCall =
    useCallback(async (): Promise<SigninWebAuthnAssertionOptionsResponse> => {
      return execute(
        async () => {
          if (!preMfaToken) throw new Error("Missing MFA token");
          const response = await getWebAuthnAssertionOptions(preMfaToken);
          if (!response || !response.challenge) throw INVALID_RESPONSE_ERROR;
          return response;
        },
        async () => {
          const { mockGetWebAuthnAssertionOptions } = await import(
            "./mockSigninApi"
          );
          return mockGetWebAuthnAssertionOptions({
            pre_mfa_token: preMfaToken || "mock_token",
          });
        }
      );
    }, [execute, preMfaToken]);

  const verifyWebAuthnAssertionCall = useCallback(
    async (
      credential: SigninVerifyWebAuthnAssertionRequest["credential"]
    ): Promise<SigninVerifyWebAuthnAssertionResponse> => {
      return execute(
        async () => {
          if (!preMfaToken) throw new Error("Missing MFA token");
          const response = await verifyWebAuthnAssertion(
            credential,
            preMfaToken
          );
          if (!response || response.status !== "ok")
            throw INVALID_RESPONSE_ERROR;

          // Success - Update Auth Context and Cookies
          processSuccessResponse(response);

          return response;
        },
        async () => {
          const { mockVerifyWebAuthnAssertion } = await import(
            "./mockSigninApi"
          );
          const response = await mockVerifyWebAuthnAssertion({
            pre_mfa_token: preMfaToken || "mock_token",
            credential,
          });
          if (response.user) processSuccessResponse(response);
          return response;
        }
      );
    },
    [execute, preMfaToken, setUser]
  );

  const getRedirectUrl = useCallback(
    async (): Promise<void> => {
      // If the backend returns JSON {"status":"ok","redirect_to":"..."}, 
      // we must fetch it to extract the URL instead of navigating directly.
      try {
        const response = await fetch(`${env.apiBaseUrl}${API_ENDPOINTS.SIGNIN_REDIRECT}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.redirect_to) {
            window.location.href = data.redirect_to;
            return;
          }
        }
      } catch (err) {
        console.warn("Failed to fetch redirect JSON, falling back to direct navigation", err);
      }

      // Fallback: Direct browser navigation if fetch fails or doesn't return JSON
      window.location.href = `${env.apiBaseUrl}${API_ENDPOINTS.SIGNIN_REDIRECT}`;
    },
    []
  );

  return {
    loading,
    error,
    clearError,
    identify,
    loginWithPassword,
    forceChangePassword,
    initMfaSetup,
    completeMfaSetup,
    generateBackupCodes,
    confirmBackupCodesSaved,
    getMfaMethods: getMfaMethodsCall,
    verifyTotp,
    verifyBackupCode,
    getWebAuthnAssertionOptions: getWebAuthnAssertionOptionsCall,
    verifyWebAuthnAssertion: verifyWebAuthnAssertionCall,
    skipMfa: skipMfaCall,
    getRedirectUrl,
  };
}
